import React, { useEffect, useState } from "react";
import { X, Zap, Rocket, Layers, Coins, AlertTriangle } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { tokensToBRL, formatBRL } from "../lib/currency";

const PRESETS = [1, 5, 10, 25, 50, 100];
const MAX_MULTI = 100;

/**
 * MultiSendDialog — modal do "Disparo Múltiplo".
 * Permite enviar o MESMO pedido em até 100 conversas simultâneas.
 */
export default function MultiSendDialog({ open, onClose, value, onApply }) {
    const { tokensInfo } = useAuth();
    const [count, setCount] = useState(value || 1);
    // Controle de animação de entrada/saída
    const [render, setRender] = useState(open);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (open) {
            setCount(value || 1);
            setRender(true);
            const raf = requestAnimationFrame(() =>
                requestAnimationFrame(() => setShow(true))
            );
            return () => cancelAnimationFrame(raf);
        }
        setShow(false);
        const t = setTimeout(() => setRender(false), 250);
        return () => clearTimeout(t);
    }, [open, value]);

    if (!render) return null;

    const taskCost = tokensInfo?.task_cost ?? 50;
    const balance = tokensInfo?.tokens ?? 0;
    const unlimited = !!tokensInfo?.unlimited;
    const totalCost = count * taskCost;
    const maxAllowed = unlimited ? MAX_MULTI : Math.min(MAX_MULTI, Math.floor(balance / taskCost));
    const overBudget = !unlimited && count > maxAllowed;

    const apply = () => {
        if (overBudget) return;
        onApply(count);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6"
            data-testid="multi-send-dialog"
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-250 ease-out ${show ? "opacity-100" : "opacity-0"}`}
                style={{ transitionDuration: "250ms" }}
                onClick={onClose}
            />

            <div
                className={`relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-[#0d0d0d] border border-border rounded-lg shadow-2xl ${show ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}`}
                style={{ transitionProperty: "opacity, transform", transitionDuration: "250ms", transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-md bg-primary/15 border border-primary/30 flex items-center justify-center">
                            <Layers className="w-4.5 h-4.5 text-primary" strokeWidth={2.2} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="font-display text-base font-bold tracking-tight">Disparo Múltiplo</h2>
                                <span className="bg-primary text-white text-[8px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-sm">
                                    Novo
                                </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                                O mesmo pedido, várias conversas de uma vez.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="multi-dialog-close"
                        className="p-2 -mr-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors shrink-0"
                        aria-label="Fechar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Explicação */}
                    <div className="border border-border rounded-md bg-[#0a0a0a] p-3.5 space-y-2">
                        <ExplainRow icon={<Zap className="w-3.5 h-3.5 text-primary" />}>
                            Ao enviar, seu pedido é disparado em <b className="text-foreground">{count > 1 ? `${count} conversas novas` : "várias conversas"}</b> ao mesmo tempo — cada uma gera uma resposta independente.
                        </ExplainRow>
                        <ExplainRow icon={<Layers className="w-3.5 h-3.5 text-primary" />}>
                            Perfeito para gerar <b className="text-foreground">variações</b>: imagens, textos e ideias diferentes do mesmo pedido.
                        </ExplainRow>
                        <ExplainRow icon={<Coins className="w-3.5 h-3.5 text-primary" />}>
                            Máximo de <b className="text-foreground">100 por vez</b> · cada conversa consome <b className="text-foreground">{taskCost} tokens</b>.
                        </ExplainRow>
                    </div>

                    {/* Contador grande */}
                    <div className="text-center">
                        <div className="font-display text-5xl font-black tracking-tighter text-primary" data-testid="multi-count-display">
                            {count}<span className="text-2xl align-top">x</span>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
                            {count === 1 ? "conversa (modo normal)" : "conversas simultâneas"}
                        </div>
                    </div>

                    {/* Slider */}
                    <input
                        type="range"
                        min={1}
                        max={MAX_MULTI}
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                        data-testid="multi-slider"
                        className="w-full accent-[#22C55E] cursor-pointer"
                        aria-label="Quantidade de conversas"
                    />

                    {/* Presets */}
                    <div className="grid grid-cols-6 gap-1.5">
                        {PRESETS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => setCount(p)}
                                data-testid={`multi-preset-${p}`}
                                className={`py-1.5 rounded-sm text-xs font-mono font-bold transition-colors border ${
                                    count === p
                                        ? "bg-primary text-white border-primary"
                                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                                }`}
                            >
                                {p}x
                            </button>
                        ))}
                    </div>

                    {/* Custo */}
                    <div className="border border-border rounded-md bg-[#0a0a0a] divide-y divide-border text-sm">
                        <div className="flex items-center justify-between px-3.5 py-2.5">
                            <span className="text-muted-foreground text-xs">Custo total</span>
                            <span className="font-mono font-semibold" data-testid="multi-total-cost">
                                {totalCost.toLocaleString("pt-BR")} tokens
                                <span className="text-muted-foreground font-normal"> · {formatBRL(tokensToBRL(totalCost))}</span>
                            </span>
                        </div>
                        <div className="flex items-center justify-between px-3.5 py-2.5">
                            <span className="text-muted-foreground text-xs">Seu saldo</span>
                            <span className="font-mono font-semibold">
                                {unlimited ? "∞" : `${balance.toLocaleString("pt-BR")} tokens`}
                                {!unlimited && <span className="text-muted-foreground font-normal"> · até {Math.max(maxAllowed, 0)}x</span>}
                            </span>
                        </div>
                    </div>

                    {overBudget && (
                        <div className="flex items-start gap-2 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-md px-3 py-2.5" data-testid="multi-over-budget">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                            <span>
                                Saldo insuficiente para {count}x. Seu saldo atual permite no máximo{" "}
                                <b>{Math.max(maxAllowed, 0)}x</b>. Recarregue para liberar mais disparos.
                            </span>
                        </div>
                    )}

                    {/* CTA */}
                    <button
                        type="button"
                        onClick={apply}
                        disabled={overBudget}
                        data-testid="multi-apply-btn"
                        className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Rocket className="w-4 h-4" strokeWidth={2.2} />
                        {count === 1 ? "Usar modo normal (1x)" : `Ativar disparo ${count}x`}
                    </button>
                    <p className="text-[10px] text-center text-muted-foreground -mt-2">
                        O disparo acontece quando você enviar a mensagem.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ExplainRow({ icon, children }) {
    return (
        <div className="flex items-start gap-2.5 text-xs text-muted-foreground leading-snug">
            <span className="mt-0.5 shrink-0">{icon}</span>
            <span>{children}</span>
        </div>
    );
}
